import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import Chart from '../chart';
import useAutoRefresh from '../hooks/useAutoRefresh';

const SEGMENTS = [
  { key: 'operational', label: 'Operational Time', description: 'Systems running normally', color: '#34d399', accent: 'from-emerald-400/80 to-emerald-500/60' },
  { key: 'planned',     label: 'Planned Downtime', description: 'Scheduled maintenance windows', color: '#fbbf24', accent: 'from-amber-300/80 to-orange-400/60' },
  { key: 'unplanned',   label: 'Unplanned Downtime', description: 'Unexpected outages and faults', color: '#f87171', accent: 'from-rose-400/80 to-rose-500/60' },
];

export default function DowntimeChart() {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { theme } = useTheme();

  // Initialize with zeros so the chart renders immediately
  const [metrics, setMetrics] = useState({ operational: 0, planned: 0, unplanned: 0 });

  // Fetch latest once
  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`/api/equipment_status.php?latest=1&_=${Date.now()}`);
      const json = await res.json();
      json.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = json[0];

      const parseHours = (start, end) => {
        if (!start || !end) return 0;
        const s = new Date(`1970-01-01T${start}Z`);
        const e = new Date(`1970-01-01T${end}Z`);
        const diff = (e - s) / 3600000;
        return diff > 0 ? diff : 0;
      };

      const planned = parseHours(latest?.planned_downtime_start, latest?.planned_downtime_end);
      const unplanned = parseHours(latest?.unplanned_downtime_start, latest?.unplanned_downtime_end);
      const operational = Math.max(24 - planned - unplanned, 0);

      setMetrics({ operational, planned, unplanned });
    } catch (e) {
      console.error(e);
      setMetrics({ operational: 0, planned: 0, unplanned: 0 });
    }
  }, []);

  useAutoRefresh(fetchLatest, []);

  // (Re)create the chart (theme affects tooltip colors)
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    chartRef.current?.destroy();

    const values = SEGMENTS.map(s => metrics[s.key]);

    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: SEGMENTS.map(s => s.label),
        datasets: [
          {
            data: values,
            backgroundColor: SEGMENTS.map(s => s.color),
            hoverBackgroundColor: SEGMENTS.map(s => s.color),
            borderWidth: 0,
            borderRadius: 18,
            spacing: 6,
            hoverOffset: 14,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, // fill container rect
        animation: { duration: 600, easing: 'easeOutQuart' },
        layout: { padding: 12 },
        cutout: '72%',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc',
            titleColor: theme === 'dark' ? '#e2e8f0' : '#0f172a',
            bodyColor: theme === 'dark' ? '#cbd5f5' : '#1e293b',
            borderWidth: 1,
            borderColor: theme === 'dark' ? '#1e293b' : '#e2e8f0',
            padding: 12,
            callbacks: {
              label: (context) => {
                const total = context.dataset.data.reduce((sum, v) => sum + v, 0);
                const value = context.raw;
                const percent = total ? Math.round((value / total) * 100) : 0;
                return `${context.label}: ${Number(value).toFixed(1)} hrs (${percent}%)`;
              },
            },
          },
          datalabels: { display: false },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [theme]); // recreate on theme change

  // Update dataset when metrics change
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.data.datasets[0].data = SEGMENTS.map(s => metrics[s.key]);
    chart.update();
  }, [metrics]);

  const segments = useMemo(
    () => SEGMENTS.map(s => ({ ...s, value: metrics[s.key] ?? 0 })),
    [metrics]
  );

  const totalHours = useMemo(
    () => segments.reduce((sum, s) => sum + s.value, 0),
    [segments]
  );

  const uptimePercent = useMemo(() => {
    if (!totalHours) return 0;
    return Math.round(((metrics.operational ?? 0) / totalHours) * 100);
  }, [metrics.operational, totalHours]);

  return (
    // Outer wrapper must have height from the panel; keep a tiny floor to make the donut visible during layout
    <div className="relative flex h-full min-h-[220px] overflow-hidden">
      {/* LEFT: donut area claims leftover space; 'basis-0 flex-1 min-w-0' prevents collapse */}
      <div className="relative basis-0 flex-1 min-w-0 min-h-0">
        {/* Normal block so the canvas inherits non-zero height */}
        <div className="h-full w-full">
          <canvas ref={canvasRef} className="h-full w-full" />
        </div>
        {/* Center labels overlay */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-slate-400">Uptime</span>
          <span className="text-4xl font-semibold text-white">{totalHours ? `${uptimePercent}%` : '--'}</span>
          <span className="text-xs text-slate-400">{totalHours ? 'Last 24 hours' : 'Awaiting telemetry'}</span>
        </div>
      </div>

      {/* RIGHT: metrics column, fixed width; scrolls internally if tight */}
      <div className="flex w-64 shrink-0 flex-col gap-3 overflow-auto p-3">
        {segments.map((segment) => {
          const percent = totalHours ? Math.round((segment.value / totalHours) * 100) : 0;
          return (
            <div
              key={segment.key}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-3 shadow-inner shadow-slate-950/20 transition hover:border-white/20 hover:bg-white/10 dark:border-white/5"
            >
              <div className={`pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br opacity-60 blur-2xl transition group-hover:opacity-90 ${segment.accent}`} />
              <div className="relative z-10 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">{segment.label}</span>
                </div>
                <span className="text-base font-semibold text-white">{segment.value.toFixed(1)} hrs</span>
                <span className="text-xs text-slate-400">{percent}% · {segment.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
